import { FiscalChangeSection, FiscalCurrentBilling, FiscalOnboardingStatus, FiscalPillTone, FiscalPlanUsage, FiscalWizardStepKey } from '../models';

/** Ruta de la página Fiscal (menú Configuración › Fiscal, píldora del Home, modal de enviado). */
export const FISCAL_ONBOARDING_ROUTE = '/fiscal';

/** Paquetes de números de control («Ampliar talonario» de la tarjeta «Facturas de tu plan»): página Mejora tu Fina. */
export const FISCAL_PACKAGES_ROUTE = '/comercio/mejora-tu-fina';

export const FISCAL_ONBOARDING_ETA_LABEL = 'hasta 3 semanas';

export const FISCAL_DOCUMENT_MAX_MB = 10;
export const FISCAL_IMAGE_MAX_MB = 2;
export const FISCAL_DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
export const FISCAL_IMAGE_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg';
export const FISCAL_LOGO_ACCEPT = '.png,image/png';

/**
 * 409 del primer `PUT /api/fiscal-onboarding/business` cuando el comercio ya factura con UniDigital sin expediente
 * (ADM-834): no se crea el borrador; el asistente se cierra y la página pasa a la vista Activo.
 */
export const FISCAL_ALREADY_ACTIVE_CODE = 'FISCAL_ALREADY_ACTIVE';

/** Copys del diseño «Módulo Fiscal v2». */
export const FISCAL_ONBOARDING_COPY = {
    pageTitle: 'Fiscal',
    pageSubtitle: 'Facturación digital válida ante el SENIAT.',
    landing: {
        title: 'Activa tu **facturación digital** y emite facturas válidas ante el SENIAT desde tus ventas, solo con tu ==RIF y tu logo==.',
        subtitle: `El proceso de activación tarda ${FISCAL_ONBOARDING_ETA_LABEL}.`,
        videoEyebrow: 'Ver cómo funciona',
        videoLabel: 'Video de 2 min',
        ctaStart: 'Activar facturación digital',
        ctaContinue: 'Continuar activación',
        /** Sesión de soporte (el asociado ni siquiera llega aquí): solo el dueño gestiona la activación. */
        onlyOwner: 'Solo el dueño del comercio puede iniciar la activación fiscal.',
    },
    howItWorks: [
        { n: '1', title: 'Cuéntanos sobre tu negocio', text: 'Persona natural o jurídica, cómo facturas hoy y tu tipo de contribuyente.' },
        { n: '2', title: 'Sube tus documentos', text: 'Te pedimos solo los que aplican a tu negocio, agrupados por categoría.' },
        { n: '3', title: 'Firma y activa', text: `Revisamos, firmas la solicitud de autorización y en ${FISCAL_ONBOARDING_ETA_LABEL} facturas en digital.` },
    ],
    resume: {
        title: 'Seguimos donde lo dejaste',
        text: 'Guardamos tu avance. Te falta completar el paso marcado.',
        cta: 'Continuar donde quedé',
        onlyOwner: 'Solo el dueño del comercio puede continuar la activación fiscal.',
        rowsLabel: 'Avance de tu activación',
        stalled: (n: number, label: string) => `Te falta en el paso ${n} · ${label}`,
        pending: 'Pendiente',
        complete: 'Completo',
        toSend: 'Por enviar',
        missing: (n: number) => (n === 1 ? 'Falta 1 dato' : `Faltan ${n} datos`),
    },
    wizard: {
        title: 'Activación fiscal',
        subtitles: {
            negocio: 'Cuéntanos sobre tu negocio para pedirte solo lo que aplica.',
            identidadNatural: 'Tus documentos de identidad y domicilio.',
            identidadJuridica: 'Documentos de la empresa y de cada socio.',
            actividad: 'Lo que respalda tu actividad y la marca de tus facturas.',
            confirmar: `Revisa y envía. La activación tarda ${FISCAL_ONBOARDING_ETA_LABEL}.`,
            enviado: 'Tu solicitud quedó registrada.',
        } as Record<string, string>,
        stepText: (i: number, total: number) => `Paso ${i} de ${total} · se guarda automáticamente`,
        missing: (text: string) => `Falta: ${text}`,
        cancel: 'Cancelar',
        closeLabel: 'Cerrar y guardar',
        back: 'Atrás',
        next: 'Continuar',
        submit: 'Enviar solicitud',
        savedProgress: 'Guardamos tu avance',
        doneTitle: '¡Solicitud enviada!',
        doneText: `La activación tarda ${FISCAL_ONBOARDING_ETA_LABEL}. Te avisamos en Fina y por correo en cada paso.`,
        doneCta: 'Ir a Fiscal',
        /** Respaldo del 409 `FISCAL_ALREADY_ACTIVE` si el api no trae título o mensaje (ADM-834). */
        alreadyActiveTitle: 'Facturación ya activa',
        alreadyActiveText: 'Tu comercio ya emite sus números de control con UniDigital.',
    },
    business: {
        personQuestion: '¿Qué tipo de persona eres?',
        taxpayerQuestion: 'Tipo de contribuyente',
        billingQuestion: '¿Cómo facturas hoy?',
        natural: 'Natural',
        juridica: 'Jurídica',
        ordinario: 'Ordinario',
        especial: 'Especial',
        professionalTitle: 'Presto servicios profesionales',
        professionalText: 'Abogado, médico, ingeniero, arquitecto, contador u otra profesión colegiada.',
        regulatedTitle: 'Mi actividad está regulada por CONATEL, SUDEBAN o BCV',
        regulatedText: 'Por ejemplo, venta de puntos de venta o proveedores de internet.',
        pathTo: 'Digital',
        askList: (n: number) => `Te vamos a pedir ${n} documentos:`,
        changePersonTitle: '¿Cambiar el tipo de persona?',
        changePersonText: 'Se borran los documentos que ya subiste porque el checklist cambia. ¿Quieres continuar?',
        changePersonConfirm: 'Sí, cambiar',
    },
    documents: {
        ready: (done: number, total: number) => `${done} de ${total} listos`,
        listo: 'LISTO',
        upload: 'Subir',
        change: 'Cambiar',
        remove: 'Quitar',
        addPartner: '+ Agregar socio',
        helpOpen: '¿Cómo lo obtengo?',
        helpClose: 'Ocultar pasos',
        numberingTitle: 'Numeración',
        lastControlLabel: 'Último número de control',
        lastControlHelp: 'El último que emitiste con tu imprenta digital actual. Seguimos desde el siguiente.',
        lastControlPlaceholder: '00-00000000',
        /** FINA-3656: 8 dígitos exactos (antes decía «hasta 8» y se rellenaba con ceros). */
        lastControlInvalid: 'Escribe el número como 00-00000000 (8 dígitos).',
        uploaded: 'Archivo subido',
        removed: 'Archivo eliminado',
        logoHint: 'Al menos 500 px. Aparece en tus facturas.',
        replaced: 'REEMPLAZADO',
        removeReplacement: 'Quitar el reemplazo',
    },
    /** Datos de la carta al SENIAT que se piden en el paso «Identidad y empresa» (quien firma, Registro Mercantil, actividad). */
    letter: {
        representativeTitle: 'Representante legal',
        representativeHelp: 'Quien firma la solicitud ante el SENIAT, tal como aparece en el acta constitutiva.',
        selfTitle: 'Tus datos',
        selfHelp: 'Como aparecen en tu cédula y en tu RIF; con ellos se redacta la solicitud ante el SENIAT.',
        name: 'Nombre completo',
        namePlaceholder: 'Nombre y apellido',
        idNumber: 'Cédula de identidad',
        idPlaceholder: 'V-12345678',
        idError: 'Escribe la cédula como V-12345678 (V o E y de 5 a 9 dígitos).',
        role: 'Cargo',
        rolePlaceholder: 'Presidente, Director, Gerente…',
        registryTitle: 'Registro Mercantil',
        registryHelp: 'Datos de inscripción de la empresa, tal como aparecen en el acta constitutiva.',
        office: 'Oficina de registro',
        officeHelp: 'Se imprime como «Registro Mercantil …».',
        officePlaceholder: 'Segundo',
        jurisdiction: 'Circunscripción judicial',
        jurisdictionHelp: 'Se imprime como «de la Circunscripción Judicial …».',
        jurisdictionPlaceholder: 'del Distrito Capital y Estado Miranda',
        date: 'Fecha de inscripción',
        number: 'Número',
        numberPlaceholder: '12',
        volume: 'Tomo',
        volumePlaceholder: '45-A',
        activityTitle: 'Actividad principal',
        activityHelp: 'Tal como aparece en el acta constitutiva (objeto principal). La solicitud ante el SENIAT la cita.',
        activityLabel: 'Actividad principal del negocio',
        activityPlaceholder: 'la compra, venta y reparación de…',
    },
    confirm: {
        summaryTitle: 'Resumen',
        today: 'Hoy',
        passTo: 'Pasas a',
        person: 'Persona',
        taxpayer: 'Contribuyente',
        nextInvoice: 'Próxima factura',
        documents: 'Documentos',
        attached: (done: number, total: number) => `${done} de ${total} adjuntos`,
        contactTitle: '¿Dónde te avisamos?',
        contactText: 'Te contamos aquí cada avance de tu solicitud.',
        email: 'Correo',
        phone: 'Teléfono',
        phoneVerified: 'Teléfono verificado',
        phonePending: 'Necesitamos verificarlo para enviar tu solicitud. Te llegará un código por SMS.',
        notMine: 'No es mi número',
        confirmation: 'Confirmo que mis datos son correctos y autorizo a Fina a solicitar mi facturación digital ante el SENIAT.',
        confirmationDigital: (next: string) => `Confirmo que mis datos son correctos y que continúo mi numeración en ${next}.`,
        onlyOwner: 'Solo el dueño del comercio puede enviar la solicitud.',
    },
    review: {
        title: 'Recibimos tu solicitud',
        reviewing: `Estamos revisando tus documentos. El proceso completo tarda ${FISCAL_ONBOARDING_ETA_LABEL} y te avisamos en Fina y por correo en cada paso.`,
        signed: 'Firmaste tu solicitud. La estamos gestionando ante el SENIAT; te avisamos en cuanto tu facturación digital esté activa.',
        steps: { sent: 'Solicitud enviada', docs: 'Revisión de documentos', sign: 'Firma de la solicitud', seniat: 'Activación SENIAT' },
        inProgress: 'En curso',
        approved: 'Aprobados',
        signedShort: 'Firmada',
        inFina: 'En Fina',
        eta: 'Hasta 3 semanas',
        now: 'Ahora',
        nowDocs: { title: 'Revisión de documentos', text: 'El equipo de Fina está validando tus documentos. Luego te pediremos tu firma.' },
        nowSeniat: { title: 'Activación ante el SENIAT', text: 'Estamos gestionando tu autorización. Te avisamos en cuanto esté activa.' },
        activeBy: 'Activa a más tardar',
        /** FINA-3655: el PDF firmado (`GET /authorization/download`) también en revisión/activación y en Activo. */
        downloadSigned: 'Descargar solicitud firmada',
        downloadFailed: 'No se pudo abrir la solicitud firmada',
        sent: 'Lo que enviaste',
        attached: 'Documentos adjuntos',
        stepsLabel: 'Progreso de tu solicitud',
        /** Filas de «Lo que enviaste», en el orden del prototipo. */
        summaryKeys: { billing: 'Facturas hoy con', passTo: 'Pasas a', passToValue: 'Digital', person: 'Persona', taxpayer: 'Contribuyente', nextInvoice: 'Próxima factura' },
    },
    corrections: {
        title: (n: number) => `Tu solicitud necesita ${n} ${n === 1 ? 'corrección' : 'correcciones'}`,
        text: 'Lo demás quedó bien. Sube de nuevo estos archivos y reenvía tu solicitud.',
        reupload: 'Subir de nuevo',
        fixed: 'CORREGIDO',
        fixedText: 'Archivo actualizado.',
        resendHint: 'Al reenviar, retomamos la revisión de tus documentos.',
        resend: 'Reenviar a revisión',
        resent: 'Solicitud reenviada',
        onlyOwner: 'Solo el dueño del comercio puede corregir y reenviar la solicitud.',
    },
    sign: {
        docTitle: 'Solicitud de autorización',
        download: 'Descargar PDF',
        title: 'Firma tu solicitud',
        text: 'Revisamos tus documentos y todo está en orden. Firma la solicitud para que la enviemos al SENIAT.',
        draw: 'Dibujar',
        upload: 'Subir imagen',
        drawHere: 'Dibuja tu firma aquí',
        drawHint: 'Usa el mouse o el dedo.',
        clear: 'Limpiar',
        uploadTitle: 'Sube una imagen de tu firma',
        uploadHint: 'PNG o JPG con fondo claro',
        sealTitle: 'Sello de la empresa',
        sealUpload: 'Sube la imagen del sello',
        sealHint: 'PNG o JPG con fondo claro o transparente',
        sealAction: 'Subir sello',
        change: 'Cambiar',
        submit: 'Firmar y enviar',
        signed: 'Solicitud firmada y enviada',
        onlyOwner: 'Solo el dueño del comercio puede firmar la solicitud.',
        previewUpdating: 'Actualizando vista previa…',
        previewFailed: 'No se pudo actualizar la vista previa con tu firma',
        /** FINA-3664: el api rechazó la firma o el sello del borrador; no se puede firmar con ellos. */
        previewRejected: 'El api rechazó tu firma o tu sello. Reemplázalos para poder firmar y enviar.',
    },
    active: {
        title: 'Ya estás ==facturando==',
        nextControl: 'Próximo número de control',
        profile: 'Perfil',
        changeRequest: 'Solicitar cambio',
        taxpayer: 'Contribuyente',
        modality: 'Modalidad',
        modalityValue: 'Digital',
        person: 'Persona',
        format: 'Formato',
        formatValue: 'Predeterminado',
    },
    /** «Solicitar cambio» (pantalla Activo): asistente en modo cambio y avisos del Perfil. */
    change: {
        title: 'Solicitud de cambio de perfil',
        stepLabel: 'Qué cambias',
        subtitles: {
            cambio: 'Elige qué datos de tu perfil fiscal quieres actualizar.',
            negocio: 'Actualiza los datos de tu negocio.',
            identidad: 'Reemplaza solo los documentos que cambiaron.',
            actividad: 'Reemplaza solo los documentos que cambiaron.',
            confirmar: 'Revisa y envía. La revisión tarda hasta 5 días hábiles.',
            enviado: 'Tu solicitud quedó registrada.',
        } as Record<string, string>,
        stepText: (i: number, total: number) => `Paso ${i} de ${total}`,
        missingSections: 'elegir qué quieres cambiar',
        missingConfirm: 'confirmar tus datos',
        noChanges: 'algún cambio respecto a tu perfil actual',
        /** FINA-3659: faltantes de un paso que no se eligió en «Qué cambias» (bloquean el envío igual). */
        missingInStep: (items: string, step: string) => `${items} (en ${step})`,
        submit: 'Enviar solicitud de cambio',
        doneTitle: '¡Solicitud de cambio enviada!',
        doneText: 'La revisamos en hasta 5 días hábiles. Mientras tanto sigues facturando con tu perfil actual.',
        doneCta: 'Volver a Fiscal',
        discarded: 'Descartaste la solicitud de cambio',
        question: '¿Qué quieres cambiar?',
        reasonLabel: 'Motivo del cambio',
        reasonOptional: '(opcional)',
        reasonPlaceholder: 'Ej.: mudamos la sede fiscal o actualizamos el RIF',
        reasonMax: 160,
        info: 'Tus datos actuales ya están cargados: solo reemplaza lo que cambió. Mientras revisamos, sigues facturando con tu perfil actual.',
        summaryLabel: 'Solicitas cambiar',
        confirmation: 'Confirmo que los datos nuevos son correctos y autorizo a Fina a actualizar mi perfil fiscal.',
        changePersonText: 'El checklist cambia: tendrás que adjuntar los documentos del nuevo tipo y se descartan los reemplazos que ya subiste en esta solicitud. ¿Quieres continuar?',
        pendingTitle: 'Solicitud de cambio en revisión',
        pendingText: (sent: string, sections: string) => `${sent} · ${sections}. Sigues facturando con tu perfil actual.`,
        sentToday: 'Enviada hoy',
        sentOn: (date: string) => `Enviada el ${date}`,
        rejectedTitle: 'Tu solicitud de cambio no fue aprobada',
        rejectedText: (date: string, note: string) => `${date ? `Revisada el ${date} · ` : ''}${note}`,
    },
    /** Tarjeta «Facturas de tu plan» de la pantalla Activo (saldo del talonario). */
    plan: {
        title: 'Facturas de tu plan',
        expand: 'Ampliar talonario',
        available: 'Disponibles',
        used: 'Usadas',
        invoices: 'Facturas',
        debitNotes: 'Notas de débito',
        creditNotes: 'Notas de crédito',
        of: (total: number) => `de ${total}`,
        note: 'Cada documento usa un número del talonario. Te avisamos cuando quede el 10%.',
        aria: (p: FiscalPlanUsage | null) =>
            p ? `${p.available} de ${p.total} números disponibles. Usados: ${p.invoices} facturas, ${p.debitNotes} notas de débito y ${p.creditNotes} notas de crédito.` : '',
    },
    homePill: {
        start: 'Activa tu facturación digital',
        inProgress: 'Activación fiscal',
    },
    video: {
        title: 'Cómo funciona la facturación digital',
        placeholder: '[VIDEO DE ACTIVACIÓN FISCAL]',
    },
    errors: {
        load: 'No se pudo cargar tu expediente fiscal',
        format: 'Formato no soportado',
        formatDoc: 'Formatos aceptados: PDF, JPG, PNG',
        formatImage: 'Formatos aceptados: PNG, JPG',
        formatLogo: 'El logo debe ser un PNG',
        size: (mb: number) => `El archivo supera el tamaño máximo de ${mb} MB`,
    },
};

/** Tarjetas del paso «Qué cambias» (prototipo: etiqueta, descripción e icono de 20 px, trazo 1.6). */
export const FISCAL_CHANGE_SECTIONS: { key: FiscalChangeSection; label: string; text: string; icon: string }[] = [
    { key: 'negocio', label: 'Tu negocio', text: 'Tipo de persona o de contribuyente.', icon: 'M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M9 20v-6h6v6' },
    {
        key: 'identidad',
        label: 'Identidad y empresa',
        text: 'RIF, cédula, domicilio fiscal o socios.',
        icon: 'M3 5h18v14H3z M7 10a2 2 0 1 0 4 0a2 2 0 1 0-4 0 M5.5 16c.6-1.6 2-2.5 3.5-2.5s2.9.9 3.5 2.5 M14 9h5 M14 13h4',
    },
    { key: 'actividad', label: 'Actividad y marca', text: 'Certificado SENIAT, licencia o logo.', icon: 'M4 4h16v16H4z M4 16l5-5 4 4 3-3 4 4 M15 9h.01' },
];

export const FISCAL_WIZARD_STEPS: { key: FiscalWizardStepKey; label: string }[] = [
    { key: 'negocio', label: 'Tu negocio' },
    { key: 'identidad', label: 'Identidad y empresa' },
    { key: 'actividad', label: 'Actividad y marca' },
    { key: 'confirmar', label: 'Confirmar' },
];

export const FISCAL_CURRENT_BILLING_OPTIONS: { value: FiscalCurrentBilling; label: string; text: string; note: string }[] = [
    { value: 'maquina', label: 'Máquina fiscal', text: 'O impresora térmica homologada', note: 'Dejas la máquina fiscal y empiezas en digital.' },
    { value: 'libre', label: 'Forma libre', text: 'Talonario con números de control', note: 'Dejas el talonario. El número de control se asigna solo.' },
    { value: 'digital', label: 'Digital', text: 'Con otra imprenta digital', note: 'Cambias de imprenta y sigues tu numeración.' },
    { value: 'nofacturo', label: 'Aún no facturo', text: 'Será tu primera factura', note: 'Tu primera factura ya será digital.' },
];

/** Píldora por estado (pantalla, Home y menú). `error` nunca llega al comercio (el api lo muestra como `en_activacion`). */
export const FISCAL_STATUS_PILLS: Record<FiscalOnboardingStatus, { label: string; tone: FiscalPillTone }> = {
    borrador: { label: 'Falta información', tone: 'danger' },
    en_revision: { label: 'En revisión', tone: 'warn' },
    requiere_correccion: { label: 'Requiere corrección', tone: 'danger' },
    pendiente_firma: { label: 'Falta tu firma', tone: 'danger' },
    en_activacion: { label: 'En revisión', tone: 'warn' },
    activo: { label: 'Activo', tone: 'success' },
    error: { label: 'En revisión', tone: 'warn' },
};

/** Texto del paso de verificación de teléfono (reusa `fina-phone-verification` de Fina Presta con otro copy). */
export const FISCAL_PHONE_COPY = {
    stepLabel: 'Teléfono',
    verifyTitle: 'Verifica tu teléfono',
    verifySubtitle: 'Necesitamos verificarlo para enviar tu solicitud. Te llegará un código por SMS o WhatsApp.',
};
